import { BuildCanvas } from "./BuildCanvas.js";
import { PhaseTimeline } from "./PhaseTimeline.js";
import { Transcript } from "./Transcript.js";
import { describeElapsed } from "../../lib/labels.js";
import { useElapsed } from "../../lib/useElapsed.js";
import type { CanvasState } from "../../lib/viewModel.js";
import { useSessionStream } from "../../lib/useSessionStream.js";
import { useOffersBackend } from "../../lib/xanoHandoff.js";

/**
 * Strictly read-only.
 *
 * There is deliberately no control here that sends anything to the agent — the
 * API exposes no such route either, so the guarantee holds by the absence of
 * surface on both sides rather than by a check. What this view offers is
 * visibility: where the build is, what it is doing right now, what it has
 * produced, and eventually a link.
 *
 * The frame is exactly viewport height. That is not styling: with a minimum
 * height instead of a fixed one, the panes grow the document rather than
 * scrolling inside it, and a long build becomes an unbounded page where the
 * whole history is on screen at once and "now" is impossible to find.
 */
export function BuildView({ sessionId, onRestart }: { sessionId: string; onRestart: () => void }) {
  const stream = useSessionStream(sessionId);
  const failed = stream.outcome !== null && stream.outcome !== "succeeded";
  const elapsed = useElapsed(stream.createdAt, stream.isTerminal);
  const split = stackedSplit(stream.canvas.state);
  // Decided in lib so every skin answers it the same way — see
  // lib/xanoHandoff.ts.
  const hasBackend = useOffersBackend({
    outcome: stream.outcome,
    xanoTenant: stream.xanoTenant,
  });

  return (
    <div className="flex h-dvh flex-col overflow-hidden">
      <header className="shrink-0 border-b border-rule bg-surface px-5 py-3">
        <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
          <div className="flex min-w-0 items-baseline gap-4">
            <h1 className="font-display text-xl">Hatch</h1>
            {stream.prompt && (
              // Four minutes into a build the user has lost the thread of what
              // they asked for; the prompt is the answer to "what is this?".
              <p className="min-w-0 truncate text-sm text-ink-faint" title={stream.prompt}>
                {stream.prompt}
              </p>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-4 text-xs text-ink-faint">
            {elapsed !== null && <span className="tabular-nums">{describeElapsed(elapsed)}</span>}
            {!stream.isTerminal && stream.connection === "reconnecting" && (
              // Quiet by design: a brief drop during a long build is normal,
              // and an error banner would alarm the user about a self-healing
              // gap.
              <span>Reconnecting…</span>
            )}
          </div>
        </div>

        <div className="mt-3">
          <PhaseTimeline phase={stream.phase} failed={failed} />
        </div>
      </header>

      {/*
        `min-h-0` on the row and on each pane is load-bearing. A flex child
        defaults to a content-derived minimum size and refuses to shrink below
        it, which makes an inner `overflow-y-auto` do nothing at all — the exact
        shape of the unbounded-page bug this layout replaced.

        Stacked below the breakpoint with the transcript on top, since that is
        the surface that moves.

        Stacked, the panes take turns rather than splitting the height — see
        `stackedSplit`.
      */}
      <div className="flex min-h-0 flex-1 flex-col-reverse lg:flex-row">
        <div className={`${PANE} lg:grow-[3] ${split.canvas}`}>
          <BuildCanvas
            canvas={stream.canvas}
            currentAction={stream.currentAction}
            sessionId={sessionId}
            hasBackend={hasBackend}
            onRestart={onRestart}
          />
        </div>

        <div className={`${PANE} lg:grow-[2] ${split.transcript}`}>
          <Transcript steps={stream.steps} isTerminal={stream.isTerminal} />
        </div>
      </div>
    </div>
  );
}

/**
 * What both panes share, in either layout.
 *
 * `grow-0` and the animated `flex-basis` are the stacked mechanism: with growth
 * off, basis alone decides how the column is divided, and basis is the one size
 * property that animates cleanly on a flex child. `lg:basis-0` plus a `grow`
 * value on each pane hands the row back to the desktop 3:2 split, where the
 * transition is inert because nothing changes the basis.
 *
 * `overflow-hidden` clips a pane while it is closing, so its content slides out
 * of view instead of spilling past a shrinking box.
 */
const PANE =
  "flex min-h-0 grow-0 flex-col overflow-hidden transition-[flex-basis,opacity] duration-500 ease-out motion-reduce:transition-none lg:basis-0 lg:opacity-100";

/**
 * How the stacked layout divides the viewport, by what the canvas is showing.
 *
 * A phone has room for one pane, not two. Half each left the transcript too
 * short to read as a feed and the preview too short to read as a site, so
 * neither pane did its job. So they take turns:
 *
 *   nest — nothing to preview yet, so the transcript owns the viewport. This is
 *   the whole build, and it is what the user is actually watching.
 *
 *   framing — the site is up, which is what they came for. The transcript hands
 *   the viewport over; the preview is now the screen.
 *
 *   closed — no preview to promote, and the card carries the only way forward.
 *   Split rather than swapped, because on a failure the transcript is the
 *   evidence and taking it away leaves the user with a verdict and no account
 *   of it.
 *
 * Animated rather than cut: a cut reads as a navigation, and this is one screen
 * resolving into its result.
 */
function stackedSplit(state: CanvasState["state"]): { canvas: string; transcript: string } {
  switch (state) {
    case "nest":
      return { canvas: "basis-0 opacity-0", transcript: "basis-full opacity-100" };
    case "framing":
      return { canvas: "basis-full opacity-100", transcript: "basis-0 opacity-0" };
    case "closed":
      return { canvas: "basis-1/2 opacity-100", transcript: "basis-1/2 opacity-100" };
  }
}
